import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { Home, NotebookPen, Clock, User, LogOut, Shield } from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/lib/AuthContext";

const navItems = [
  { to: "/", label: "Today", icon: Home, testId: "nav-today" },
  { to: "/notes", label: "Notes", icon: NotebookPen, testId: "nav-notes" },
  { to: "/library", label: "History", icon: Clock, testId: "nav-history" },
  { to: "/preferences", label: "Profile", icon: User, testId: "nav-profile" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isGuest = !user;

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className={`min-h-screen ${isGuest ? "flex flex-col" : "flex flex-col lg:flex-row"}`} style={{ backgroundColor: "var(--ink-canvas)" }} data-testid="user-shell">
      {/* Desktop sidebar (lg+) — hidden for guests */}
      {!isGuest && (
      <aside className="hidden lg:flex w-64 xl:w-72 shrink-0" style={{ borderRight: "1px solid var(--ink-border)", background: "var(--ink-surface)" }}>
        <div className="flex flex-col w-full p-6">
          <div className="flex items-center gap-3 mb-10">
            <Logo size={40} />
            <div>
              <div className="text-[10px] tracking-[0.22em] uppercase font-semibold" style={{ color: "var(--ink-muted)" }}>
                {user?.name ? `Hi, ${user.name.split(" ")[0]}` : "Welcome"}
              </div>
              <h1 className="font-serif text-2xl leading-none mt-1" style={{ color: "var(--ink-heading)" }}>InThink</h1>
            </div>
          </div>

          <nav className="flex flex-col gap-1" data-testid="desktop-nav">
            {navItems.map(({ to, label, icon: Icon, testId }) => (
              <NavLink
                key={to}
                to={to}
                end={to === "/"}
                data-testid={`desktop-${testId}`}
                className={({ isActive }) =>
                  `inline-flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-colors`
                }
                style={({ isActive }) => ({
                  background: isActive ? "var(--ink-elevated)" : "transparent",
                  color: isActive ? "var(--ink-light)" : "var(--ink-secondary)",
                })}
              >
                <Icon strokeWidth={1.6} className="w-4 h-4" />
                {label}
              </NavLink>
            ))}
            {user?.role === "admin" && (
              <NavLink
                to="/admin"
                data-testid="desktop-link-admin"
                className="inline-flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-colors mt-4 pt-4"
                style={{ color: "var(--ink-secondary)", borderTop: "1px solid var(--ink-border)" }}
              >
                <Shield strokeWidth={1.6} className="w-4 h-4" />
                Admin console
              </NavLink>
            )}
          </nav>

          <div className="mt-auto pt-6" style={{ borderTop: "1px solid var(--ink-border)" }}>
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <div className="text-sm truncate font-medium" style={{ color: "var(--ink-light)" }}>{user?.name}</div>
                <div className="text-xs truncate" style={{ color: "var(--ink-muted)" }}>{user?.email}</div>
              </div>
              <button
                onClick={doLogout}
                data-testid="desktop-logout"
                aria-label="Sign out"
                className="inline-flex items-center justify-center w-9 h-9 rounded-full transition-colors shrink-0"
                style={{ border: "1px solid var(--ink-border)", background: "var(--ink-elevated)", color: "var(--ink-secondary)" }}
              >
                <LogOut className="w-3.5 h-3.5" strokeWidth={1.7} />
              </button>
            </div>
          </div>
        </div>
      </aside>
      )}

      {/* Top header — visible on all breakpoints when guest, mobile-only when signed in */}
      <header className={`${isGuest ? "" : "lg:hidden"} w-full pt-8 pb-4`}>
        <div className={`${isGuest ? "max-w-5xl" : "max-w-md"} mx-auto px-6 flex items-center justify-between gap-3`}>
          <div className="flex items-center gap-3 min-w-0">
            <Logo size={40} />
            <div className="min-w-0">
              {!isGuest && (
                <div className="text-[10px] tracking-[0.22em] uppercase font-semibold" style={{ color: "var(--ink-muted)" }}>
                  {user?.name ? `Hi, ${user.name.split(" ")[0]}` : "Welcome"}
                </div>
              )}
              <h1 className="font-serif text-3xl leading-none mt-1 truncate" style={{ color: "var(--ink-heading)" }} data-testid="app-title">
                InThink
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {isGuest ? (
              <Link
                to="/login"
                data-testid="header-signin-btn"
                className="inline-flex items-center rounded-full px-4 h-9 text-sm font-semibold transition-colors"
                style={{
                  background: "#F5F3EE",
                  color: "#141428",
                  border: "1px solid #F5F3EE",
                  letterSpacing: "0.01em",
                }}
              >
                Sign in
              </Link>
            ) : (
              <>
                {user?.role === "admin" && (
                  <NavLink
                    to="/admin"
                    data-testid="link-admin"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-colors text-xs font-medium"
                    style={{ border: "1px solid var(--ink-border)", background: "var(--ink-elevated)", color: "var(--ink-secondary)" }}
                    title="Admin"
                  >
                    <Shield className="w-3.5 h-3.5" strokeWidth={1.7} /> Admin
                  </NavLink>
                )}
                <button
                  onClick={doLogout}
                  data-testid="logout-button"
                  aria-label="Sign out"
                  className="inline-flex items-center justify-center w-9 h-9 rounded-full transition-colors"
                  style={{ border: "1px solid var(--ink-border)", background: "var(--ink-elevated)", color: "var(--ink-secondary)" }}
                >
                  <LogOut className="w-3.5 h-3.5" strokeWidth={1.7} />
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      <main className={`flex-1 w-full ${isGuest ? "pb-16" : "pb-40 sm:pb-28 lg:pb-12"} ${!isGuest ? "lg:pt-10" : ""}`}>
        <div className={`${isGuest ? "max-w-2xl" : "max-w-md lg:max-w-5xl xl:max-w-6xl"} mx-auto px-4 sm:px-6 lg:px-10`}>
          <Outlet />
        </div>
      </main>

      {/* Mobile bottom nav (under lg) — hidden for guests */}
      {!isGuest && (
      <nav
        className="lg:hidden fixed bottom-0 left-0 right-0 z-40"
        style={{
          background: "var(--ink-canvas)",
          borderTop: "1px solid var(--ink-border)",
          paddingBottom: "env(safe-area-inset-bottom, 0)",
        }}
        data-testid="bottom-nav"
      >
        <div className="max-w-md mx-auto flex items-stretch justify-around py-2">
          {navItems.map(({ to, label, icon: Icon, testId }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={testId}
              className="inline-flex flex-col items-center justify-center gap-1 flex-1 py-2 transition-colors"
              style={({ isActive }) => ({
                color: isActive ? "var(--ink-secondary)" : "var(--ink-muted)",
              })}
            >
              <Icon strokeWidth={1.7} className="w-5 h-5" />
              <span className="text-[10px] font-medium">{label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
      )}
    </div>
  );
}
